import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { formatWorkDuration, getWorkMinutesPerDay } from '../../../util/dates';
import _ from 'lodash';

import NoData from '../../../components/NoData/NoData';

import { DatePicker } from '../../../components/Filter/components';
import DashboardInfo from '../../../components/DashboardInfo/DashboardInfo';
import { getSelectionValues } from '../../../util/filterUtils';

import CFDChart from '../components/CFD';
import CTSPChart from '../components/CTSP';
import CTBChart from '../components/CTB';
import CTHChart from '../components/CTH';
import CTAChart from '../components/CTA';
import TRCChart from '../components/TRC';
import Widget from '../components/Widget';
import { WALLBOARD_WIDGETS as ALL_WIDGETS, WALLBOARD_WIDGET_IDS as ALL_WIDGET_IDS } from '../OverviewConstants';
import { GridStackManager } from '../../../util/gridStackUtils';
import chartUtils from '../../../util/chartUtils';
import { WALLBOARD_HEIGHT, getWallboardFrame } from '../../../util/wallboardFrame';

// Import Styles
import './../Overview.scss';

const ROWS_BOTTOM_SPACE = 20;
const HEADER_HEIGHT_BEFORE_LAYOUT = 120;

class Overview extends Component {
  constructor(props) {
    super(props);
    this.gridRef = React.createRef();
    // Calculate dynamic cell height based on screen resolution for wallboard
    const viewportHeight = getWallboardFrame(window.innerWidth, window.innerHeight).height;
    const headerHeight = HEADER_HEIGHT_BEFORE_LAYOUT;
    const availableHeight = viewportHeight - headerHeight;
    
    // Calculate estimated rows based on actual widget configuration
    const maxRow = Math.max(...Object.values(ALL_WIDGETS).map(w => w.y || 0));
    const maxHeight = Math.max(...Object.values(ALL_WIDGETS).map(w => w.h || 1));
    const estimatedRowCount = maxRow + maxHeight; // Total grid height
    const dynamicCellHeight = Math.floor(availableHeight / estimatedRowCount) - 15;
    
    this.gridManager = new GridStackManager(this.gridRef, {
      cellHeight: Math.max(dynamicCellHeight, 150), // Minimum 150px, but scale with screen
      widgetConfig: ALL_WIDGETS,
      widgetIdPrefix: 'widget-',
      fixedRows: true
    });
    this.gridManager.setDefaultWidgetOrder(ALL_WIDGET_IDS);
    this.grid = null;
    
    this.state = {
      oct: 0,
      ct: 0,
      wip: 0,
      wipAvgAge: 0
    };
  }

  componentDidMount() {
    this.loadGridStack().then(() => {
      this.forceLayoutUpdate(() => {
          this.handleResize(() => {
            setTimeout(() => {
              window.dispatchEvent(new Event('resize'));
            }, 100); 
          });
      });
    });

    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.gridManager.destroy();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.ctsp != this.props.ctsp) {
      const eightyfifth = _.find(this.props.ctsp.percentiles, p => p.name == '85');
      if (eightyfifth) {
        this.setState({
          ct: eightyfifth.rawValue
        });
      }

      const overviewEightyFifth = _.find(this.props.ctsp.percentiles, p => p.name == '85');
      this.setState({
        oct: overviewEightyFifth ? overviewEightyFifth.rawValue : 0
      });
    }

    if (prevProps.cta != this.props.cta) {
      const wipAvgAge = _(this.props.cta.series)
        .flatMap('data')
        .meanBy('cycleTimeInMinutes');

      this.setState({
        wipAvgAge
      });
    }

    if (prevProps.cfd != this.props.cfd) {
      let wip = 0;
      _(this.props.cfd)
        .filter(l => {
          return (
            this.props.dashboard.todo.indexOf(l.listId) === -1 && this.props.dashboard.done.indexOf(l.listId) === -1
          );
        })
        .forEach(l => {
          let last = _.last(l.data);
          wip += last.daily;
        });

      this.setState({
        wip
      });
    }

    const showedFlowEfficiency = getSelectionValues(prevProps.queueLists, prevProps.allQueueLists).length < prevProps.allQueueLists.length;
    const showsFlowEfficiency = getSelectionValues(this.props.queueLists, this.props.allQueueLists).length < this.props.allQueueLists.length;
    if (showedFlowEfficiency !== showsFlowEfficiency) {
      this.handleResize();
    }

    if (!_.isEqual(prevProps.selectedWidgets, this.props.selectedWidgets)) {
      const availableWidgets = this.getAvailableWidgets();
      const newSelectedWidgets = this.props.selectedWidgets ? 
        this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId)) : 
        availableWidgets;
      this.gridManager.handleGoBackFromCustomize(newSelectedWidgets, () => this.forceUpdate());
    }
  }

  async loadGridStack() {
    this.grid = await this.gridManager.loadGridStack();
    this.applyInitialLayout();
  }

  applyInitialLayout = () => {
    if (this.grid) {
      this.forceLayoutUpdate();
    }
  };

  handleResize = (callback) => {
    if (this.grid && this.gridRef.current) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (this.grid && this.gridRef.current) {
          this.forceLayoutUpdate();
          // getBoundingClientRect measures the frame as scaled to the screen
          const frame = getWallboardFrame(window.innerWidth, window.innerHeight);
          const viewportHeight = frame.height;
          const headerHeight = this.gridRef.current.getBoundingClientRect().top / frame.scale || HEADER_HEIGHT_BEFORE_LAYOUT;
          const availableHeight = viewportHeight - headerHeight - ROWS_BOTTOM_SPACE;
          const availableWidgets = this.getAvailableWidgets();
          const selectedWidgets = this.props.selectedWidgets && this.props.selectedWidgets.length > 0
            ? this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId))
            : availableWidgets;
          
          const { queueLists, allQueueLists } = this.props;
          const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
          
          let actualRowCount = 1; 
          if (this.grid.engine && this.grid.engine.nodes && this.grid.engine.nodes.length > 0) {
            actualRowCount = this.grid.engine.nodes.reduce((max, node) => {
              return Math.max(max, (node.y || 0) + (node.h || 1));
            }, 1);
          } else {
            const filteredWidgets = selectedWidgets.filter(widgetId => {
              if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
                return false;
              }
              return true;
            });
            actualRowCount = Math.ceil(filteredWidgets.length / 3);
          }
          
          const finalCellHeight = Math.floor(availableHeight / actualRowCount);
          
          this.grid.cellHeight(finalCellHeight);

          this.forceLayoutUpdate(callback);
        }
      }, 150);
    } else if (callback && typeof callback === 'function') {      
      callback();
    }
  };

  forceLayoutUpdate = (callback) => {
    if (this.grid) {
      const availableWidgets = this.getAvailableWidgets();
      const selectedWidgets = this.props.selectedWidgets && this.props.selectedWidgets.length > 0
        ? this.props.selectedWidgets.filter(widgetId => availableWidgets.includes(widgetId))
        : availableWidgets;
      
      this.gridManager.handleGoBackFromCustomize(
        [...selectedWidgets],
        () => {
          this.forceUpdate();
          if (callback && typeof callback === 'function') {
            callback();
          }
        }
      );
    }
  };

  getAvailableWidgets = () => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    if (!showFlowEfficiency) {
      return ALL_WIDGET_IDS.filter(widgetId => widgetId !== 'flow_efficiency_metric');
    }
    return ALL_WIDGET_IDS;
  };

  isWidgetVisible = (widgetId) => {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    
    if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
      return false;
    }
    
    const selectedWidgets = this.props.selectedWidgets;
    
    if (!selectedWidgets || selectedWidgets.length === 0) {
      return true;
    }
    
    if (Array.isArray(selectedWidgets)) {
      return selectedWidgets.includes(widgetId);
    }
    
    return true;
  };

  getWidgetProps = (widgetId, showFlowEfficiency) => {
    const { dashboard } = this.props;
    const widgetInfo = ALL_WIDGETS[widgetId];
    if (!widgetInfo) return null;

    const props = { ...widgetInfo };

    if (['cycle_time_metric', 'wip_metric', 'process_metrics'].includes(widgetId)) {
      props.w = showFlowEfficiency ? 3 : 4;
    }

    if (props.linkTo) {
      props.linkTo = `/dashboard/${dashboard._id}/${props.linkTo}`;
    }

    return props;
  }

  getWidgetContent = (widgetId) => {
    switch (widgetId) {
      case 'cycle_time_metric':
        return (
          <React.Fragment>
            <div className='info-box-item'>
              <h4>{formatWorkDuration(this.state.oct, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays)} or less</h4>
              <p>85th-percentile cycle time</p>
            </div>
          </React.Fragment>
        );
      case 'wip_metric':
        return (
          <React.Fragment>
            <div className='info-box-item'>
              <h4>
                {this.state.wip} work item{this.state.wip != 1 ? 's' : ''}
              </h4>
              <p>Currently in progress</p>
            </div>
            <div className='info-box-item'>
              <h4>
                {formatWorkDuration(this.state.wipAvgAge, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays)}
              </h4>
              <p>Average age</p>
            </div>
          </React.Fragment>
        );
      case 'flow_efficiency_metric':
        return (
          <div className='info-box-item'>
            <h4>{this.props.avgFe.avgFe || '0.00'}%</h4>
            <p>Date range average</p>
          </div>
        );
      case 'process_metrics':
        return this.renderSystemMetrics();
      case 'cfd_chart':
        return (
          <React.Fragment>
            {this.props.cfd.length > 0 &&
              <div className='cell-content'>
                <CFDChart id="Overview_CFD" className="chart" data={this.props.cfd} />
              </div>
            }
            <NoData
              className="cell-content overview_CFD"
              shape="area"
              show={!this.props.cfd.length}
              loading={this.props.cfdLoading}
            />
          </React.Fragment>
        );
      case 'ctsp_chart':
        return (
          <React.Fragment>
            {(this.props.ctsp.series && this.props.ctsp.series.length > 0) &&
              <div className='cell-content'>
                <CTSPChart id="Overview_CTSP" className="chart" data={this.props.ctsp} />
              </div>
            }
            <NoData
              className="cell-content overview_CTSP"
              shape="scatter"
              show={this.props.ctsp.series && !this.props.ctsp.series.length}
              loading={this.props.ctspLoading}
            />
          </React.Fragment>
        );
      case 'ctb_chart':
        return (
          <React.Fragment>
            {(this.props.ctb.breakdown && this.props.ctb.breakdown.length > 0) &&
              <div className='cell-content'>
                <CTBChart id="Overview_CTB" className="chart" data={this.props.ctb.breakdown} />
              </div>
            }
            <NoData
              className="cell-content overview_CTB"
              shape="columns"
              show={this.props.ctb.breakdown && !this.props.ctb.breakdown.length}
              loading={this.props.ctbLoading}
            />
          </React.Fragment>
        );
      case 'cth_chart':
        return (
          <React.Fragment>
            {(this.props.cth.series && this.props.cth.series.length > 0) &&
              <div className='cell-content'>
                <CTHChart id="Overview_CTH" className="chart" data={this.props.cth} />
              </div>
            }
            <NoData
              className="cell-content overview_CTH"
              shape="columns"
              show={this.props.cth.series && !this.props.cth.series.length}
              loading={this.props.cthLoading}
            />
          </React.Fragment>
        );
      case 'cta_chart':
        return (
          <React.Fragment>
            {(this.props.cta.series && this.props.cta.series.length > 0) &&
              <div className='cell-content'>
                <CTAChart id="Overview_CTA" className="chart" data={this.props.cta} />
              </div>
            }
            <NoData
              className="cell-content overview_CTA"
              shape="scatter"
              show={this.props.cta.series && !this.props.cta.series.length}
              loading={this.props.ctaLoading}
            />
          </React.Fragment>
        );
      case 'trc_chart':
        return (
          <React.Fragment>
            {chartUtils.hasValues(this.props.trc) &&
              <div className='cell-content'>
                <TRCChart id="Overview_TRC" className="chart" data={this.props.trc} />
              </div>
            }
            <NoData 
              className="cell-content overview_TRC"
              shape="columns"
              show={!chartUtils.hasValues(this.props.trc)} 
              loading={this.props.trcLoading} 
            />
          </React.Fragment>
        );
      default:
        return null;
    }
  }

  renderSystemMetrics() {
    let stats = _.first(this.props.cfdStats);

    let systemAvgArrivalRate = stats ? stats.avgArrivalRate : 0.0;
    let systemAvgThroughput = stats ? stats.avgThroughput : 0.0;
    let systemAvgDailyWIP = stats ? stats.avgDailyWIP : 0;
    let systemAvgCycleTime = formatWorkDuration(
      (stats ? stats.avgCycleTime : 0) * getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays),
      this.props.excludeWeekendOpts,
      this.props.timePrecision,
      this.props.useWorkingHoursInCycleTimeDays
    );

    return (
      <div className='info-box-content'>
        <div className='info-box-item'>
          <h4>{systemAvgArrivalRate.toFixed(2)} items/day</h4>
          <p>Arrival rate</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgThroughput.toFixed(2)} items/day</h4>
          <p>Throughput</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgDailyWIP.toFixed(2)} items</h4>
          <p>Average WIP</p>
        </div>
        <div className='info-box-item'>
          <h4>{systemAvgCycleTime}</h4>
          <p>Average cycle time</p>
        </div>
      </div>
    );
  }

  render() {
    const { queueLists, allQueueLists } = this.props;
    const showFlowEfficiency = getSelectionValues(queueLists, allQueueLists).length < allQueueLists.length;
    const availableWidgets = this.getAvailableWidgets();
    
    // Determine which widgets to render
    let widgetsToRender = this.props.selectedWidgets;
    if (!widgetsToRender || widgetsToRender.length === 0) {
      widgetsToRender = availableWidgets;
    } else {
      // Filter out unavailable widgets from selected widgets
      widgetsToRender = widgetsToRender.filter(widgetId => availableWidgets.includes(widgetId));
    }

    // Calculate actual number of rows based on rendered widgets
    const renderedWidgetRows = widgetsToRender
      .filter(widgetId => {
        if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
          return false;
        }
        return true;
      })
      .map(widgetId => ALL_WIDGETS[widgetId]?.row || 1);
    
    const actualRowCount = Math.max(...renderedWidgetRows, 1);

    return (
      <div className='content-wrap overview'>
        <div className='overview-header'>
          <div className='title-info'>
            <p>Overview</p>
            <DashboardInfo dashboard={this.props.dashboard} />
          </div>
        
          <div className='overview-header-right-elements'>
            <DatePicker />
          </div>
        </div>
        
        <div 
          className={`grid-stack wallboard-grid`}
          ref={this.gridRef}
          style={{
            height: `calc(var(--wallboard-height, ${WALLBOARD_HEIGHT}px) - 40px)`,
            width: '100%',
            overflow: 'hidden',
            '--actual-rows': actualRowCount
          }}
        >
          {widgetsToRender.filter(widgetId => {
            // Filter out flow efficiency widget when not available
            if (widgetId === 'flow_efficiency_metric' && !showFlowEfficiency) {
              return false;
            }
            return true;
          }).map(widgetId => {
            const widgetProps = this.getWidgetProps(widgetId, showFlowEfficiency);
            if (!widgetProps) return null;

            const isVisible = this.isWidgetVisible(widgetId);
            const style = !isVisible ? { display: 'none' } : {};

            return (
              <Widget
                key={widgetId}
                id={`widget-${widgetId}`}
                title={widgetProps.title}
                w={widgetProps.w}
                h={widgetProps.h}
                isMetric={widgetProps.isMetric}
                customizeMode={false}
                showFlowEfficiency={showFlowEfficiency}
                style={style}
                linkTo={widgetProps.linkTo}
              >
                {this.getWidgetContent(widgetId)}
              </Widget>
            );
          })}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    enabledColors: state.filters.enabledColors,
    timePrecision: state.filters.timePrecision,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    queueLists: state.filters.overview.selectedQueueLists,
    allQueueLists: state.filters.allQueueLists,
    hash: state.filters.overview.hash,

    overviewLoading: state.app.overviewLoading,
    dataIsLoading: state.app.isLoading,
    dataHash: state.app.dataHash,

    dashboard: state.dashboards.selected,

    cfd: state.overview.cfd,
    cfdLoading: state.overview.cfdLoading,
    cfdStats: state.overview.cfdStats,
    ctsp: state.overview.ctsp,
    ctspLoading: state.overview.ctspLoading,
    ctb: state.overview.ctb,
    ctbLoading: state.overview.ctbLoading,
    cth: state.overview.cth,
    cthLoading: state.overview.cthLoading,
    cta: state.overview.cta,
    ctaLoading: state.overview.ctaLoading,
    trc: state.overview.trc,
    trcLoading: state.overview.trcLoading,
    avgFe: state.overview.avgFe,
    selectedWidgets: state.filters.overview.selectedWidgets,
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default connect(mapStateToProps)(Overview);
